import React from 'react';
import { Button, Icon, Tooltip } from '@material-ui/core';
import { FloatClear, InlineIcon } from '@styles/common';
import { Link } from '@components/link';
import { StatDisplay } from '@screens/players/stat.display';
import { StatIcon } from '@screens/players/stat.icon';
import { encodeCid } from '@domain/utils/cid.utils';
import { getVipDate } from '@domain/utils/vip.info.utils';
import { formatStringDate } from '@domain/utils/format.utils';
import { vipDescriptions } from '@data/vip.descriptions';
import { PlayerDataProps } from '@screens/players/types/player.data.props';
import { GetPraiaGame } from '@domain/game.selection/types/game.praia';
import { useGame } from '@domain/game.selection/game.selection.hook';
import { GameType } from '@domain/game.selection/types/game.type';
import { StatsContainer } from './stats.container';
import { ButtonArea, MainInfoArea, NameArea, PlayerAvatar, PlayerListItemContainer } from './player.list.item.styles';

interface PlayerListItemProps extends PlayerDataProps {
  listItem?: boolean;
  children?: React.ReactNode;
}

const PlayerListItem = ({ player, children, listItem = false }: PlayerListItemProps) => {
  const cidTxt = `CID: ${encodeCid(player.pid)}`;
  const name = player.firstName || player.lastName ? `${player.firstName || ''} ${player.lastName || ''}` : player.nickname || '';
  const isPraiaGame = useGame().type === GameType.Praia;

  // doc: https://developers.facebook.com/docs/graph-api/reference/user/picture/
  const pictureUrl = player.uid ? `https://graph.facebook.com/${player.uid}/picture?type=normal` : '';

  return (
    <PlayerListItemContainer $highlight={listItem}>
      <PlayerAvatar alt={name} src={pictureUrl} />

      <ButtonArea>
        {listItem && (
          <Button variant='contained' color='primary' size='small' component={Link} to={`/player/${player.pid}`} endIcon={<Icon>settings</Icon>}>
            Details
          </Button>
        )}
      </ButtonArea>

      {children}

      <NameArea>
        {name}{' '}
        {player.sex && (
          <Tooltip title={`Gender: ${player.sex}`} aria-label={player.sex}>
            <InlineIcon>{player.sex}</InlineIcon>
          </Tooltip>
        )}
      </NameArea>
      <MainInfoArea>
        <Tooltip title={cidTxt} aria-label={cidTxt}>
          <StatDisplay label='PID'>{player.pid}</StatDisplay>
        </Tooltip>
      </MainInfoArea>
      <FloatClear />

      <StatsContainer>
        {player.birthdate && (
          <StatDisplay label='Birthdate'>
            {player.birthdate} {player.age && `(${player.age}yo)`}
          </StatDisplay>
        )}
        {(player.country || player.city) && (
          <StatDisplay label='From'>
            {player.country} {player.country && player.city && '-'} {player.city}
            {typeof player.timezone === 'number' && ` (UTC ${player.timezone >= 0 ? '+' : ''}${player.timezone})`}
          </StatDisplay>
        )}
        {isPraiaGame && player.appcodes && <StatDisplay label='Game'>{GetPraiaGame(player.appcodes)}</StatDisplay>}
      </StatsContainer>

      <PlayerInventory player={player} />
      <PlayerVipInfo player={player} />
    </PlayerListItemContainer>
  );
};

const PlayerInventory = ({ player }: PlayerDataProps) => {
  const inventory = Object.entries(player.inventory);

  if (!inventory || !inventory.length) return null;

  return (
    <StatsContainer outlined>
      {inventory.map(([key, value]) => (
        <StatDisplay key={key} label={key} icon={`icon${key}`}>
          {value}
        </StatDisplay>
      ))}
    </StatsContainer>
  );
};

const PlayerVipInfo = ({ player }: PlayerDataProps) => {
  const { vipInfo } = player;

  if (!vipInfo) return null;

  const { stars, expireDate } = vipInfo;

  const description = vipDescriptions[stars];
  const formattedVipDate = getVipDate(vipInfo);
  const formattedExpireDate = expireDate ? formatStringDate(expireDate) : '';

  const tooltipContent: string[] = [];

  if (stars <= 0) tooltipContent.push('Not VIP');
  if (formattedVipDate) tooltipContent.push(`since ${formattedVipDate}`);
  if (formattedExpireDate) tooltipContent.push(`expires ${formattedExpireDate}`);

  return (
    <StatsContainer outlined>
      <Tooltip title={tooltipContent.join(', ')}>
        <StatDisplay label='VIP'>
          {stars <= 0 && <InlineIcon>remove</InlineIcon>}
          {stars > 0 && (
            <>
              <StatIcon icon={`gem${stars}`} alt={description} /> {description} (#{stars})
            </>
          )}
        </StatDisplay>
      </Tooltip>
      <StatDisplay label='Available Rocklets'>{vipInfo.consumableRocklets}</StatDisplay>
      <StatDisplay label='Level Rocklets'>{vipInfo.levelRocklets}</StatDisplay>
      <StatDisplay label='Maintenance Rocklets'>{vipInfo.maintenanceRocklets}</StatDisplay>
    </StatsContainer>
  );
};

export default PlayerListItem;
